import { action, makeObservable } from 'mobx';

import * as Models from 'Models/Entities';
import { FreightContactEntity, FreightHandlingEntity, UserEntity } from 'Models/Entities';
import { bookingStatus, bookingTransactionStatus, bookingType } from 'Models/Enums';
import { store } from 'Models/Store';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { getMeasurementLabelFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { roundToDecimals } from 'Util/_HumanWritten/NumberUtils';
import { getFullName, upperCaseFirst } from 'Util/StringUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { DisplayUnits } from 'Util/UnitOfMeasurementUtils';
import { formatWeight, parseWeightUnit } from 'Util/Weight';
import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import { getDefaultPassengerInfo } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import {
	convertToCubicString,
	convertToWeightString,
	getCubicPerUnitFromFreightDetail,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Shipment/CalculationHelpers';

export interface ICheckInBookingOverviewDtoDto {
	id: string;
	qrCodeId: string;
	humanReadableId: string;
	bookedSummary: BookingSummary;
	user: UserEntity;
	transactionStatus: bookingTransactionStatus;
	bookingStatus: bookingStatus;
	checkedIn: boolean;
	checkInStatusId?: string;
	isInvoiced: boolean;
	desynced: boolean;
	returnBooking?: CheckInBookingOverviewDto;
	returnBookingForId?: string;
	firstTimeOnRoute: boolean;
	hasNote: boolean;
	legacyId?: string;
	hasInvoicedAlteration?: boolean;
}

export class CheckInBookingOverviewDto implements ICheckInBookingOverviewDtoDto {
	constructor(attributes?: Partial<ICheckInBookingOverviewDtoDto>) {
		makeObservable<CheckInBookingOverviewDto, 'assignAttributes'>(this, {
			assignAttributes: action,
		});

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	private assignAttributes(attributes?: Partial<ICheckInBookingOverviewDtoDto>) {
		if (isNullOrUndefined(attributes)) {
			return;
		}
		if (isNotNullOrUndefined(attributes.id)) {
			this.id = attributes.id;
		}
		if (isNotNullOrUndefined(attributes.qrCodeId)) {
			this.qrCodeId = attributes.qrCodeId;
		}
		if (isNotNullOrUndefined(attributes.humanReadableId)) {
			this.humanReadableId = attributes.humanReadableId;
		}
		if (isNotNullOrUndefined(attributes.checkedIn)) {
			this.checkedIn = attributes.checkedIn;
		}
		if (isNotNullOrUndefined(attributes.desynced)) {
			this.desynced = attributes.desynced;
		}
		if (isNotNullOrUndefined(attributes.isInvoiced)) {
			this.isInvoiced = attributes.isInvoiced;
		}
		if (isNotNullOrUndefined(attributes.hasNote)) {
			this.hasNote = attributes.hasNote;
		}
		if (isNotNullOrUndefined(attributes.user)) {
			if (attributes.user === null) {
				this.user = attributes.user;
			} else if (attributes.user instanceof Models.UserEntity) {
				this.user = attributes.user;
			} else {
				this.user = new Models.UserEntity(attributes.user);
			}
		}
		if (isNotNullOrUndefined(attributes.bookedSummary)) {
			this.bookedSummary = new BookingSummary(attributes.bookedSummary);
		}
		if (isNotNullOrUndefined(attributes.firstTimeOnRoute)) {
			this.firstTimeOnRoute = attributes.firstTimeOnRoute;
		}
		if (isNotNullOrUndefined(attributes.bookingStatus)) {
			this.bookingStatus = attributes.bookingStatus;
		}
		if (isNotNullOrUndefined(attributes.transactionStatus)) {
			this.transactionStatus = attributes.transactionStatus;
		}
		if (isNotNullOrUndefined(attributes.returnBookingForId)) {
			this.returnBookingForId = attributes.returnBookingForId;
		}
		if (isNotNullOrUndefined(attributes.returnBooking)) {
			if (attributes.returnBooking === null) {
				this.returnBooking = attributes.returnBooking;
			} else {
				this.returnBooking = new CheckInBookingOverviewDto(attributes.returnBooking);
			}
		}
		if (isNotNullOrUndefined(attributes.checkInStatusId)) {
			this.checkInStatusId = attributes.checkInStatusId;
		}
		if (isNotNullOrUndefined(attributes.legacyId)) {
			this.legacyId = attributes.legacyId;
		}
		if (isNotNullOrUndefined(attributes.hasInvoicedAlteration)) {
			this.hasInvoicedAlteration = attributes.hasInvoicedAlteration;
		}
	}

	id: string;
	qrCodeId: string;
	humanReadableId: string;
	bookedSummary: BookingSummary;
	user: UserEntity;
	transactionStatus: bookingTransactionStatus;
	bookingStatus: bookingStatus;
	checkedIn: boolean;
	checkInStatusId?: string;
	isInvoiced: boolean;
	hasNote: boolean;
	desynced: boolean;
	returnBooking?: CheckInBookingOverviewDto;
	returnBookingForId?: string;
	firstTimeOnRoute: boolean;
	/** Only from get-booking-by-id, never the list. Drives the migrated-booking cancellation rules. */
	legacyId?: string;
	/** Any alteration invoiced — `isInvoiced` is all of them. Only from get-booking-by-id. */
	hasInvoicedAlteration?: boolean;

	/**
	 * Returns text for make of vehicle (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayVehicleMake(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.cargoType && cargoInfo.cargoType.cargoMake) {
				return cargoInfo.cargoType.cargoMake;
			}
		}

		return '-';
	}

	public getDisplayVehicleLength(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.selectedLengthId) {
				return getMeasurementLabelFromId(cargoInfo.selectedLengthId);
			}
		}

		return '-';
	}

	public getDisplayVehicleWeight(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo?.exactWeight != null) {
				const displayUnit = appConfigStore.weightDisplayUnit;
				const storedUnit = parseWeightUnit(cargoInfo.exactWeightUnit, displayUnit);
				return formatWeight(Number(cargoInfo.exactWeight), storedUnit, displayUnit);
			}
			if (cargoInfo && cargoInfo.selectedWeightId) {
				return getMeasurementLabelFromId(cargoInfo.selectedWeightId);
			}
		}

		return '-';
	}

	public getDisplayVehicleWidth(): string {
		if (this.bookedSummary) {
			const { cargoInfo, towOnInfo } = this.bookedSummary;
			if (cargoInfo) {
				// When the vehicle and trailer widths are linked they share one width, so fall back to
				// the trailer's width when the vehicle has none of its own (so both show the width).
				const widthId = appConfigStore.vehicleAndTrailerWidthLinked
					? (cargoInfo.selectedWidthId ?? towOnInfo?.selectedWidthId)
					: cargoInfo.selectedWidthId;
				if (widthId) {
					const width = store.getWidth(widthId);
					if (width) {
						return width.label;
					}
				}
			}
		}

		return '-';
	}

	/**
	 * Capacity space the vehicle occupies = length value × width capacity multiplier (rounded).
	 * Uses the same linked-width fallback as getDisplayVehicleWidth so the multiplier matches the
	 * displayed width. Returns '-' if not available.
	 */
	public getDisplayVehicleSpace(): string {
		const { cargoInfo, towOnInfo } = this.bookedSummary ?? {};
		if (cargoInfo) {
			const widthId = appConfigStore.vehicleAndTrailerWidthLinked
				? (cargoInfo.selectedWidthId ?? towOnInfo?.selectedWidthId)
				: cargoInfo.selectedWidthId;
			const space = (cargoInfo.selectedLength?.value ?? 0) * store.getWidthCapacityMultiplier(widthId ?? '');
			return `${roundToDecimals(space, 2)}`;
		}
		return '-';
	}

	/**
	 * Returns text for model of vehicle (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayVehicleModel(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.cargoType && cargoInfo.cargoType.cargoModel) {
				return cargoInfo.cargoType.cargoModel;
			}
		}

		return '-';
	}

	/**
	 * Returns text for rego of vehicle (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayVehicleRego(): string {
		if (this.bookedSummary) {
			const { cargoInfo } = this.bookedSummary;
			if (cargoInfo && cargoInfo.cargoIdentification) {
				return cargoInfo.cargoIdentification;
			}
		}

		return '-';
	}

	/**
	 * Returns text for type of trailer (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayTrailerType(): string {
		if (this.bookedSummary) {
			const { towOnInfo } = this.bookedSummary;
			if (towOnInfo && towOnInfo.towOnType) {
				return upperCaseFirst(towOnInfo.towOnType.label.toLowerCase());
			}
		}

		return '-';
	}

	/**
	 * Returns text for length of trailer (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayTrailerLength(): string {
		if (this.bookedSummary) {
			const { towOnInfo } = this.bookedSummary;
			if (towOnInfo && towOnInfo.selectedLengthId) {
				return getMeasurementLabelFromId(towOnInfo.selectedLengthId);
			}
		}

		return '-';
	}

	/**
	 * Returns text for width of trailer (if any)
	 *
	 * Returns '-' if not available.
	 */
	public getDisplayTrailerWidth(): string {
		if (this.bookedSummary) {
			const { towOnInfo, cargoInfo } = this.bookedSummary;
			if (towOnInfo) {
				// When the vehicle and trailer widths are linked the trailer follows the vehicle's
				// width, so fall back to the vehicle's width when the trailer has none of its own.
				const widthId = appConfigStore.vehicleAndTrailerWidthLinked
					? (towOnInfo.selectedWidthId ?? cargoInfo?.selectedWidthId)
					: towOnInfo.selectedWidthId;
				if (widthId) {
					const width = store.getWidth(widthId);
					if (width) {
						return width.label;
					}
				}
			}
		}

		return '-';
	}

	public getDisplayTrailerWeight(): string {
		if (this.bookedSummary) {
			const { towOnInfo } = this.bookedSummary;
			if (towOnInfo?.exactWeight != null) {
				const displayUnit = appConfigStore.weightDisplayUnit;
				const storedUnit = parseWeightUnit(towOnInfo.exactWeightUnit, displayUnit);
				return formatWeight(Number(towOnInfo.exactWeight), storedUnit, displayUnit);
			}
			if (towOnInfo && towOnInfo.selectedWeightId) {
				return getMeasurementLabelFromId(towOnInfo.selectedWeightId);
			}
		}

		return '-';
	}

	/**
	 * Capacity space the trailer occupies = length value × width capacity multiplier (rounded).
	 * Uses the same linked-width fallback as getDisplayTrailerWidth. Returns '-' if not available.
	 */
	public getDisplayTrailerSpace(): string {
		const { towOnInfo, cargoInfo } = this.bookedSummary ?? {};
		if (towOnInfo) {
			const widthId = appConfigStore.vehicleAndTrailerWidthLinked
				? (towOnInfo.selectedWidthId ?? cargoInfo?.selectedWidthId)
				: towOnInfo.selectedWidthId;
			const space = (towOnInfo.selectedLength?.value ?? 0) * store.getWidthCapacityMultiplier(widthId ?? '');
			return `${roundToDecimals(space, 2)}`;
		}
		return '-';
	}

	public hasVehicle(): boolean {
		return isNotNullOrUndefined(this.bookedSummary?.cargoInfo);
	}

	/**
	 * Resolves the name to display for this booking:
	 * - Vehicle bookings → the driver name.
	 * - Walk-on bookings → the "name on booking" (primary contact), when that feature is enabled
	 *   and set, mirroring how vehicle bookings show the driver. Falls back to the account name.
	 * - Everything else (incl. freight) → the account holder's name.
	 *
	 * Note: the account name remains searchable in check-in regardless of what's displayed
	 * (see FilterFerryBookings.findSearchTermMatch).
	 */
	private getDisplayNameParts(): { firstName: string; lastName: string } {
		const { cargoInfo, driverFirstName, driverLastName, primaryFirstName, primaryLastName } = this.bookedSummary;

		if (isNotNullOrUndefined(cargoInfo)) {
			return { firstName: driverFirstName ?? '', lastName: driverLastName ?? '' };
		}

		const isWalkOn = this.getBookingType() === 'PASSENGER_ONLY';
		if (
			isWalkOn &&
			appConfigStore.primaryBookingNameEnabled &&
			(stringNotEmpty(primaryFirstName) || stringNotEmpty(primaryLastName))
		) {
			return {
				firstName: primaryFirstName ?? this.user.firstName ?? '',
				lastName: primaryLastName ?? this.user?.lastName ?? '',
			};
		}

		return { firstName: this.user.firstName ?? '', lastName: this.user?.lastName ?? '' };
	}

	public getFullName() {
		if (!this.user || !this.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		const { firstName, lastName } = this.getDisplayNameParts();
		return getFullName(firstName, lastName, true);
	}

	public getFirstName() {
		if (!this.user || !this.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		return upperCaseFirst(this.getDisplayNameParts().firstName.toLowerCase());
	}

	public getLastName() {
		if (!this.user || !this.bookedSummary) {
			throw new Error('Booked summary missing');
		}

		return upperCaseFirst(this.getDisplayNameParts().lastName.toLowerCase());
	}

	/**
	 * Returns formatted text for adult passenger count
	 *
	 * e.g. 2x Adults
	 */
	public getDisplayAdultCount(): string {
		if (!this.bookedSummary) {
			return '- Adults';
		}
		return `${this.bookedSummary.adultPassengerTickets.length}x Adults`;
	}

	public getBookingType(): bookingType {
		let typfOfBooking: bookingType = 'PASSENGER_ONLY';
		if (isNotNullOrUndefined(this.bookedSummary.cargoInfo)) {
			typfOfBooking = 'VEHICLE';
		} else if (isNotNullOrUndefined(this.bookedSummary.freightDetail)) {
			typfOfBooking = 'FREIGHT';
		}
		return typfOfBooking;
	}

	/**
	 * Returns formatted text for child passenger count
	 *
	 * e.g. 2x Children
	 */
	public getDisplayChildCount(): string {
		if (!this.bookedSummary) {
			return '- Children';
		}
		return `${this.bookedSummary.childPassengerTickets.length}x Children`;
	}

	/**
	 * Returns formatted text for infant passenger count
	 *
	 * e.g. 2x Infants
	 */
	public getDisplayInfantCount(): string {
		if (!this.bookedSummary) {
			return '- Infants';
		}
		return `${this.bookedSummary.infantPassengerTickets.length}x Infants`;
	}

	/**
	 * Returns text for description of freight
	 */
	public getDisplayFreightDescription(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.description) {
				return freightDetail.description;
			}
		}

		return '-';
	}

	public getDisplayFreightQuantity(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.quantity) {
				return freightDetail.quantity.toString();
			}
		}

		return '-';
	}

	public getDisplayFreightWeight(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.weight) {
				return convertToWeightString(freightDetail.weight);
			}
		}
		return '-';
	}

	public getDisplayFreightCubic(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail) {
				return convertToCubicString(getCubicPerUnitFromFreightDetail(freightDetail));
			}
		}
		return '-';
	}

	public getDisplayFreightTotalWeight(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.weight && freightDetail.quantity) {
				return convertToWeightString(freightDetail.weight * freightDetail.quantity);
			}
		}
		return '-';
	}

	public getDisplayFreightTotalCubic(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.quantity) {
				return convertToCubicString(getCubicPerUnitFromFreightDetail(freightDetail) * freightDetail.quantity);
			}
		}
		return '-';
	}

	public getDisplayFreightCalculation(): string {
		if (this.bookedSummary && this.bookedSummary.freightCalculation) {
			return this.bookedSummary.freightCalculation;
		}
		return '-';
	}

	public getDisplayFreightDimensions(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.length && freightDetail.width && freightDetail.height) {
				return DisplayUnits.dimensions.format({
					length: freightDetail.length,
					width: freightDetail.width,
					height: freightDetail.height,
					unitOfMeasurement: whiteLabelStore.systemMeasurementUnit,
				});
			}
		}
		return '-';
	}

	public getDisplayFreightHandling(): string {
		if (this.bookedSummary) {
			const handlings = this.getFreightHandlingList();
			if (handlings?.length > 0) {
				return `${handlings.map(x => x.label).join(', ')}`;
			}
		}
		return '-';
	}

	public getFreightHandlingList(): FreightHandlingEntity[] {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.freightHandlings?.length > 0) {
				return freightDetail.freightHandlings.map(x => x.freightHandling);
			}
		}
		return [];
	}

	public getDisplayFreightCategory(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.freightCategory) {
				return freightDetail.freightCategory.label;
			}
		}
		return '-';
	}

	public getDisplayFreightType(): string {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.freightType) {
				return freightDetail.freightType.label;
			}
		}
		return '-';
	}

	public getFreightReceiverDetails(): FreightContactEntity | null {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.freightReceiverContact) {
				return freightDetail.freightReceiverContact;
			}
		}
		return null;
	}

	public getFreightNotifyDetails(): FreightContactEntity | null {
		if (this.bookedSummary) {
			const { freightDetail } = this.bookedSummary;
			if (freightDetail && freightDetail.freightNotifyContact) {
				return freightDetail.freightNotifyContact;
			}
		}
		return null;
	}

	static createBlank() {
		return new CheckInBookingOverviewDto({
			user: new Models.UserEntity(),
			bookedSummary: new BookingSummary({
				userFirstName: '',
				userLastName: '',
				adultPassengerTickets: [getDefaultPassengerInfo('A', false)],
				childPassengerTickets: [],
				infantPassengerTickets: [],
				passengerDTickets: [],
				passengerETickets: [],
				passengerFTickets: [],
				passengerGTickets: [],
				passengerHTickets: [],
				driverPhone: '',
				driverFirstName: '',
				driverLastName: '',
				ferryTrip: new Models.FerryTripEntity(),
				totalCost: 0,
				paidBookingPrice: 0,
				maxRefund: 0,
				nonRefundableAmount: 0,
				additionalBookingOptions: [],
				alterationContext: { cargoCharacteristics: [] },
			}),
		});
	}
}
